import { createMemoryCache } from "@algolia/client-common";
import { algoliasearch as searchClient } from "algoliasearch";
import { BoughtTogetherQuery, recommendClient } from "@algolia/recommend";
import { ProductData } from "../../5874/types";
import { getProductsByIds } from "../../bigcommerce/product";
import SearchClient from "../search-client";
import {
	RecommendationsRequest as AlgoliaRecommendationsRequest,
	RecommendationsResults as AlgoliaRecommendResults,
} from "algoliasearch";
import {
	AlgoliaSearchClientConfig,
	RecommendationsRequest,
	SearchFacet,
	SearchFacetOption,
	SearchMerchandising,
	SearchMerchandisingBanner,
	SearchMeta,
	SearchRequest,
	SearchResults,
	SearchSortByOptions,
	SearchSuggestionResults,
	SearchSuggestionsRequest,
	SortByOptions,
} from "../types";

import algoliasearchHelper, {
	SearchResults as AlgoliaSearchResults,
} from "algoliasearch-helper";
import { getBigCommerceImageUrls } from "@/lib/bigcommerce/utils/utils";
import AlgoliaSearchCache from "./algolia-search-cache";
import { isValidSearchQuery } from "../utils";

export class AlgoliaSearchClient extends SearchClient {
	private cache: AlgoliaSearchCache;

	private client: ReturnType<typeof searchClient>;

	private recommendClient: ReturnType<typeof recommendClient>;

	private indexName: string = "";

	private sortByOptions: SortByOptions = {
		default: {
			label: "Relevance",
			value: "default",
		},
		price_asc: {
			label: "Price (Low - High)",
			value: "price_asc",
		},
		price_desc: {
			label: "Price (High - Low)",
			value: "price_desc",
		},
	};

	constructor(config: AlgoliaSearchClientConfig) {
		super(config);

		if (!config.appId || !config.apiKey) {
			throw new Error("Algolia appId and apiKey are required");
		}

		let locale = config.locale;

		if (locale === "en") {
			locale = "en-US";
		}

		const indexName = `${config.baseIndex}-${config.storeHash}-${locale}`;

		this.indexName = indexName.replace(/^-/, "");

		this.cache = new AlgoliaSearchCache(this.indexName);

		this.client = searchClient(config.appId, config.apiKey, {
			responsesCache: this.cache,
		});

		this.recommendClient = recommendClient(config.appId, config.apiKey, {
			responsesCache: this.cache,
		});
	}

	public getSortBy() {
		return {
			options: this.sortByOptions,
			default: "default",
		};
	}

	protected emptyResponse(): SearchResults {
		return super.emptyResponse(this.sortByOptions);
	}

	private createSearchMeta(
		request: SearchRequest,
		results: AlgoliaSearchResults<any>,
	): SearchMeta {
		const createFacets = (): SearchFacet[] => {
			const requestFilters = request.filters || {};

			const activeFacetNames =
				results?.disjunctiveFacets?.map((facet: any) => facet.name) ?? [];

			// this determines what facest are displayed in what order
			const facetOrdering = results.renderingContent?.facetOrdering;

			const facetOrder = [...(facetOrdering?.facets?.order ?? [])];

			/** this is an object
			 * {
			 * 	facetName: {
			 * 		value: count
			 * 	}
			 * }
			 * */
			const facets = Object.entries(results.facets);

			const facetsToDisplay = facets
				.filter(([facetName]: any) => facetOrder.includes(facetName))
				.sort((a, b) => {
					const [facetA] = a;
					const [facetB] = b;

					const indexA = facetOrder.indexOf(facetA);
					const indexB = facetOrder.indexOf(facetB);

					return indexA - indexB;
				});

			const facetsToReturn: SearchFacet[] = facetsToDisplay.map(
				([facetName, facetValues]: any) => {
					const facetIsActive = activeFacetNames.includes(facetName);

					const displayOptions = facetOrdering?.values?.[facetName];

					const requestFiltersForFacet = requestFilters[facetName] || [];

					let options: SearchFacetOption[] = Object.entries(facetValues).map(
						([value, count]: any) => {
							const isActive =
								facetIsActive &&
								requestFiltersForFacet.some(
									(filter) => !filter.isBackgroundFilter && filter.value === value,
								);

							return {
								label: value,
								value: value,
								count: count,
								isActive: isActive,
								isSelected: isActive,
							};
						},
					);

					options = options.filter((option) => {
						return !displayOptions?.hide?.includes(option.value);
					});

					const displayOrder = displayOptions?.order || [];

					const sortDefinedOptions = () => {
						const sortedOptions = [...options]
							.filter((option) => {
								return displayOrder.includes(option.value);
							})
							.sort((a, b) => {
								return displayOrder.indexOf(a.value) - displayOrder.indexOf(b.value);
							});

						return sortedOptions;
					};

					const sortRemainingOptions = () => {
						// values are "count", "alpha" or "hidden"
						const sortRemaining = displayOptions?.sortRemainingBy || "count";

						if (sortRemaining === "hidden") {
							return [];
						}

						const remainingOptions = [...options].filter((option) => {
							return !displayOrder.includes(option.value);
						});

						if (sortRemaining === "count") {
							return remainingOptions.sort((a, b) => {
								return b.count - a.count;
							});
						}

						if (sortRemaining === "alpha") {
							return remainingOptions.sort((a, b) => {
								return a.label.localeCompare(b.label);
							});
						}

						return remainingOptions;
					};

					options = [...sortDefinedOptions(), ...sortRemainingOptions()];

					return {
						label: facetName,
						value: facetName,
						options: options,
						activeCount: options.filter((option) => option.isActive).length,
					};
				},
			);

			return facetsToReturn;
		};

		const totalResults = results.nbHits;
		const hitsPerPage = results.hitsPerPage;
		const page = results.page;
		const total = results.nbPages;

		const pagination = {
			page,
			total,
		};

		const itemCount = {
			from: hitsPerPage * page + 1,
			to: Math.min(hitsPerPage * (page + 1), totalResults),
			total: totalResults,
		};

		return {
			pagination: pagination,
			count: itemCount,
			facets: createFacets(),
			sortBy: this.createSortBy(request, this.sortByOptions),
		} as SearchMeta;
	}

	private createMerchandising(
		request: SearchRequest,
		results: AlgoliaSearchResults<any>,
	): SearchMerchandising {
		const renderingContent = results.renderingContent;

		const banners = renderingContent?.widgets?.banners?.map((banner) => {
			return {
				image: banner.image.urls?.[0].url,
				link: {
					url: banner.link?.url,
					target: banner.link?.target,
				},
			} as SearchMerchandisingBanner;
		});

		const redirect = (renderingContent as any)?.redirect?.url || undefined;

		return {
			banners: banners ?? [],
			redirect: redirect,
		};
	}

	private async parseResults(
		request: SearchRequest,
		results: AlgoliaSearchResults<any>,
	): Promise<SearchResults> {
		const initialProducts = results.hits.map((result: any) => {
			return {
				id: result.id,
				graphqlId: result.id,
				productId: Number(result.entityId),
				createdAt: result.createdAt,
				path: result.path,
				sku: result.sku,
				name: result.name,
				description: result.description,
				warranty: result.warranty,
				brand: {
					name: result.brand,
					path: "",
					entityId: result.brand_id,
				},
				isNotPurchasable: result.availability !== "Available",
				media: {
					images: [
						{
							isThumbnail: true,
							url: getBigCommerceImageUrls(result.imageurl),
						},
					],
				},
				inventory: {
					isInStock: result.availability === "Available",
				},
				productClass: result.custom_productclass,
			} as ProductData;
		});

		const meta = this.createSearchMeta(request, results);
		const merchandising = this.createMerchandising(request, results);

		// covert it to a plain object
		const raw = JSON.parse(JSON.stringify(results));

		return {
			products: initialProducts,
			meta: meta,
			merchandising: merchandising,
			suggestedCategoryIds: await this.getSuggestedCategories(request, results),
			tracking: {
				queryId: results.queryID,
			},
			raw,
		} as SearchResults;
	}

	private async getEnrichedProducts(
		results: AlgoliaSearchResults<any> | AlgoliaRecommendResults,
	): Promise<ProductData[]> {
		const hits = results.hits ?? [];

		const entityIds = hits.map((hit: any) => Number(hit.entityId));

		if (entityIds.length === 0) {
			return [];
		}

		const products = await getProductsByIds(entityIds);

		// sort the order so they match the algolia results order.
		// Index the positions first: the previous `entityIds.findIndex` inside
		// the comparator was O(n^2 log n) across the sort.
		const order = new Map(entityIds.map((entityId, index) => [entityId, index]));

		products.sort(
			(a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0),
		);

		return products;
	}

	private async getSuggestedCategories(
		request: SearchRequest,
		results: AlgoliaSearchResults<any>,
	): Promise<any> {
		// get catagory ids that are being used as a filter
		const filteredCategoriesIds = (request.filters?.categories_ids ?? []).map(
			(filter) => filter.value,
		);

		// get all category ids from facets, removing the ones used as a filter
		const categoriesIds = Object.keys(
			(results.facets as any).categories_ids ?? {},
		).filter((id) => !filteredCategoriesIds.includes(id));

		if (!categoriesIds.length) {
			return [];
		}

		const top5Categories = categoriesIds.slice(0, 5);

		return top5Categories;
	}

	private async performSearch(request: SearchRequest): Promise<SearchResults> {
		let sortByOption = request.sortBy
			? this.sortByOptions[request.sortBy]
			: undefined;

		const indexName = sortByOption
			? `${this.indexName}_${sortByOption.value}`
			: this.indexName;

		let page = request.page ?? 0;
		if (page > 0) {
			page -= 1;
		}

		const filtersToApply = {
			...(request.filters || {}),
			is_visible: [
				{
					value: "true",
					isBackgroundFilter: true,
				},
			],
		};

		const helper = algoliasearchHelper(this.client, indexName, {
			index: indexName,
			hitsPerPage: request.perPage ?? 36,
			page,
			query: request.query ?? "",
			disjunctiveFacets: Object.keys(filtersToApply) ?? [],
			facets: ["*"],
			clickAnalytics: true,
		});

		Object.entries(filtersToApply).forEach(([field, filters]) => {
			filters.forEach((filter) => {
				helper.addDisjunctiveFacetRefinement(field, filter.value);
			});
		});

		try {
			const results = await helper.searchOnce({
				page,
			});

			const facets = {};

			// combine the facets from the results
			results.content._rawResults.forEach((result: any) => {
				Object.assign(facets, result.facets);
			});

			const combinedResults = {
				...results.content,
				facets: facets,
			} as AlgoliaSearchResults;

			return await this.parseResults(request, combinedResults);
		} catch (error: any) {
			if (error.status === 403) {
				// if the application is blocked, return an empty response
				if (error.message.toLowerCase().includes("the application is blocked")) {
					return this.emptyResponse();
				}
			}

			// if there isn't a sort, return an empty response
			if (!request.sortBy) {
				return this.emptyResponse();
			}

			// otherwise, try again without the sort
			return this.performSearch({
				...request,
				sortBy: "",
			});
		}
	}

	async search(
		request: SearchRequest,
	): Promise<[SearchResults, () => Promise<ProductData[]>]> {
		const isCategorySearch = Object.keys(request.filters || {}).includes(
			"categories_ids",
		);

		const isValidQuery = isValidSearchQuery(
			request.query ?? "",
			isCategorySearch,
		);

		if (!isValidQuery) {
			return [this.emptyResponse(), () => Promise.resolve([])];
		}

		const results = await this.performSearch(request);

		const getProducts = () => {
			return this.getEnrichedProducts(results.raw);
		};

		return [results, getProducts];
	}

	async getSearchSuggestions(request: SearchSuggestionsRequest): Promise<any> {
		const suggestionsIndex = `${this.indexName}_query_suggestions`;

		const isValidQuery = isValidSearchQuery(request.query ?? "", true);

		if (!isValidQuery) {
			return this.emptyResponse();
		}

		// and an extra incase we get a suggestion that matches the query
		const perPage = request.perPage ? request.perPage + 1 : 11;

		const helper = algoliasearchHelper(this.client, suggestionsIndex, {
			index: suggestionsIndex,
			query: request.query ?? "",
			hitsPerPage: perPage,
			clickAnalytics: true,
		});

		try {
			const results = await helper.searchOnce({
				query: request.query ?? "",
				hitsPerPage: perPage,
			});

			let suggestions = results.content.hits.map((hit) => hit.query) || [];

			const queryIndex = suggestions.indexOf(request.query);

			// if no suggestions match the query, remove the extra suggestion, only if we have more suggestions than requested
			if (queryIndex === -1 && suggestions.length > perPage - 1) {
				suggestions.pop();
			} else {
				// remove the query from the suggestions
				suggestions = suggestions.filter((_, index) => index !== queryIndex);
			}

			const hitsPerPage = results.content.hitsPerPage;
			const page = results.content.page;

			return {
				query: results.content.query,
				suggestions: suggestions,
				tracking: {
					queryId: results.content.queryID,
					startingPosition: hitsPerPage * page + 1,
				},
				raw: {},
			} as SearchSuggestionResults;
		} catch (error) {
			console.error("AlgoliaSearchClient.getSearchSuggestions", error);
			return this.emptyResponse();
		}
	}

	async getRecommendations(
		request: RecommendationsRequest,
	): Promise<[any, () => Promise<ProductData[]>]> {
		let includeObjectID = true;

		if (request.type === "trending-items" || request.type === "trending-facets") {
			includeObjectID = false;
		}

		let requestData = {
			...(includeObjectID && { objectID: request.objectID }),
			indexName: this.indexName,
			threshold: request.threshold,
			model: request.type,
			maxRecommendations: request.limit,
            // NB: the Recommend API takes filters under `queryParameters`,
            // not at the top level. A stray top-level `filters` is silently
            // dropped, so the type cast alone would not have caught it.
            ...(request.filters && {
                queryParameters: { filters: request.filters },
            }),
		} as AlgoliaRecommendationsRequest;

		if (request.type === "bought-together") {
			requestData = {
				...requestData,
				...(request.facetFilters && {
					fallbackParameters: {
						facetFilters: request.facetFilters,
					},
				}),
			} as BoughtTogetherQuery;
		}

		const recommendations = await this.recommendClient.getRecommendations([
			requestData,
		]);

		let results = recommendations.results[0];
		const hitCount = results.hits.length;

		// if there are no hits from the recommendations request, use backup search query
		if (hitCount === 0 && request.query?.length) {
			const helper = algoliasearchHelper(this.client, this.indexName, {
				index: this.indexName,
				hitsPerPage: request.limit,
				page: 0,
				query: request.query ?? "",
				facets: ["*"],
				clickAnalytics: true,
			});

			const search = await helper.searchOnce({
				query: request.query ?? "",
				hitsPerPage: request.limit,
				page: 0,
			});

			results = search.content._rawResults[0];
		}

		const getProducts = () => {
			return this.getEnrichedProducts(results);
		};

		return [results, getProducts];
	}
}
